import React, {useState, useEffect, useRef, useCallback} from "react";
import { ProcessedTourOperator, ProcessedTripItinerary } from "../contentfulModel";
import classNames from "classnames";
// Import Swiper components for carousel
import { Swiper, SwiperSlide } from 'swiper/react';
import {Autoplay, EffectFade, Navigation, Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ArrowRightIcon from "../../assets/icons/arrowRightIcon.svg?react";
import ArrowLeftIcon from "../../assets/icons/arrowLeftIcon.svg?react";
import {Swiper as SwiperClass} from "swiper/types";
import {ItineraryMap} from "../ItineraryMap.tsx";
import SVGComponent from "../SVGComponent.tsx";

interface ItineraryTabProps {
    tourPackage: ProcessedTripItinerary;
    selectedDayIndex: number | null;
    onDayClick: (index: number) => void;
    tourOperator: ProcessedTourOperator
    // MICE: cada ficha es una única ubicación, así que no se pinta la línea de días ni "Día N"
    hideDayTimeline?: boolean
}

export const ItineraryTab: React.FC<ItineraryTabProps> = ({
                                                              tourOperator,
                                                              tourPackage,
                                                              selectedDayIndex,
                                                              onDayClick,
                                                              hideDayTimeline = false
                                                          }) => {
    const operatorColor = tourOperator.primaryColor || '#3B82F6';
    const [showControls, setShowControls] = useState(true);
    const [swiperRef, setSwiperRef] = useState<SwiperClass>();
    // Animation state
    const [animate, setAnimate] = useState(true);
    // Track when a new day is selected for animations
    const [currentDayIndex, setCurrentDayIndex] = useState(selectedDayIndex);
    const [animateRightPanel, setAnimateRightPanel] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const selectedDay = selectedDayIndex !== null && tourPackage.itinerary && tourPackage.itinerary.length > 0
        ? tourPackage.itinerary[selectedDayIndex]
        : tourPackage.itinerary?.[0];
    const mobileTimelineRef = useRef<HTMLDivElement>(null);
    const [displayItineraryMap, setDisplayItineraryMap] = useState(tourPackage.countryCodes && window.innerWidth > 768);


    const handlePrevious = useCallback(() => {
        swiperRef?.slidePrev();
    }, [swiperRef]);

    const handleNext = useCallback(() => {
        swiperRef?.slideNext();
    }, [swiperRef]);
    // Trigger right panel animation when selected day changes
    useEffect(() => {
        if (currentDayIndex !== selectedDayIndex) {
            setAnimateRightPanel(false);

            // Small delay to ensure animation restart
            setTimeout(() => {
                setAnimateRightPanel(true);
                setCurrentDayIndex(selectedDayIndex);
            }, 50);
        }
    }, [selectedDayIndex, currentDayIndex]);

    // Effect to handle cursor movement for controls visibility
    useEffect(() => {
        let timer: number | null = null;

        const handleMouseMove = () => {
            setShowControls(true);

            if (timer !== null) {
                window.clearTimeout(timer);
            }

            timer = window.setTimeout(() => {
                setShowControls(false);
            }, 3000);
        };

        window.addEventListener('mousemove', handleMouseMove);

        timer = window.setTimeout(() => {
            setShowControls(false);
        }, 3000);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (timer !== null) {
                window.clearTimeout(timer);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedDayIndex !== null && mobileTimelineRef.current) {
            const timeline = mobileTimelineRef.current;
            const dayElements = Array.from(timeline.children);
            const totalDays = dayElements.length;

            // Get selected, previous, and next day elements
            const selectedDay = dayElements[selectedDayIndex];
            const prevDay = selectedDayIndex > 0 ? dayElements[selectedDayIndex - 1] : null;
            const nextDay = selectedDayIndex < totalDays - 1 ? dayElements[selectedDayIndex + 1] : null;

            // Calculate timeline dimensions
            const timelineWidth = timeline.offsetWidth;

            // Calculate the ideal center position
            let targetScrollLeft;

            if (prevDay && nextDay) {
                // If both previous and next days exist, center the selected day
                const selectedDayLeft = selectedDay.offsetLeft;
                const selectedDayWidth = selectedDay.offsetWidth;
                targetScrollLeft = selectedDayLeft - (timelineWidth - selectedDayWidth) / 15;
            } else if (prevDay) {
                // If only previous day exists (we're at the last day)
                const prevDayLeft = prevDay.offsetLeft;
                const viewportCenter = timelineWidth / 2;
                targetScrollLeft = prevDayLeft - viewportCenter + prevDay.offsetWidth;
            } else if (nextDay) {
                // If only next day exists (we're at the first day)
                const nextDayRight = nextDay.offsetLeft + nextDay.offsetWidth;
                const viewportCenter = timelineWidth / 2;
                targetScrollLeft = nextDay.offsetLeft - viewportCenter;
            } else {
                // Just one day in the itinerary
                targetScrollLeft = selectedDay.offsetLeft - (timelineWidth - selectedDay.offsetWidth) / 2;
            }

            // Ensure scroll position is within bounds
            targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, timeline.scrollWidth - timelineWidth));

            // Apply smooth scrolling
            timeline.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth'
            });
        }
    }, [selectedDayIndex]);

    // Debounce scroll events
    const scrollTimeout = useRef<number | null>(null);

    const handleTimelineScroll = () => {
        if(isMobile) {
            if (scrollTimeout.current) {
                window.clearTimeout(scrollTimeout.current);
            }

            scrollTimeout.current = window.setTimeout(() => {
                if (!mobileTimelineRef.current) return;

                const container = mobileTimelineRef.current;
                const dayElements = Array.from(container.querySelectorAll('[data-day-index]'));
                const containerCenter = container.scrollLeft + container.offsetWidth / 2;

                // Find the day element closest to the center of the viewport
                let closestDay = null;
                let closestDistance = Infinity;

                dayElements.forEach(dayElement => {
                    const rect = dayElement.getBoundingClientRect();
                    const dayCenter = dayElement.offsetLeft + rect.width / 12;
                    const distance = Math.abs(dayCenter - containerCenter);

                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestDay = dayElement;
                    }
                });

                if (closestDay) {
                    const dayIndex = parseInt(closestDay.getAttribute('data-day-index') || '0', 10);
                    if (dayIndex !== selectedDayIndex) {
                        onDayClick(dayIndex);
                    }
                }
            }, 150); // 150ms debounce to prevent too many updates
        }
    };

// Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeout.current) {
                window.clearTimeout(scrollTimeout.current);
            }
        };
    }, []);



    const getTimeline = () => {
        if (isMobile || displayItineraryMap) {
            return (
                <div ref={mobileTimelineRef}
                     onScroll={handleTimelineScroll}
                     className={`relative flex overflow-x-auto hide-scrollbar items-center opacity-0 ${animate ? 'animate-fadeIn' : ''}`}

                >
                    {/* Horizontal line connecting the days */}
                    <div
                        className="absolute top-1/3 left-0 h-0.5"
                        style={{
                            backgroundColor: operatorColor,
                            width: `${(tourPackage.itinerary.length - 1) * 8}rem`,
                            left: '4rem',
                            right: '4rem',
                            transform: 'translateY(-50%)',
                        }}
                    ></div>
                    {tourPackage.itinerary &&
                        tourPackage.itinerary.map((day, index) => (
                            <div
                                key={index}
                                data-day-index={index}
                                className={`relative flex-shrink-0 w-32 mt-1 p-4 cursor-pointer `}
                                style={{
                                    color: index === selectedDayIndex ? operatorColor : 'inherit',
                                }}
                                onClick={() => onDayClick(index)}
                            >
                                <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center border-2 mx-auto"
                                    style={{
                                        borderColor: operatorColor,
                                        backgroundColor: index === selectedDayIndex ? operatorColor : '#F9F9F9',
                                    }}
                                ></div>
                                <div className="text-center mt-2">
                                    <div
                                        className={classNames([
                                            'text-lg',
                                            index === selectedDayIndex ? 'text-[#11181E]' : 'text-[#AFAFAF]',
                                            index === selectedDayIndex ? 'font-medium' : 'font-normal',
                                        ])}
                                    >
                  <span className={index === selectedDayIndex ? 'font-semibold' : 'font-normal'}>
                    Día {day.day}
                  </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            );
        } else {
            return (
                tourPackage.itinerary &&
                tourPackage.itinerary.map((day, index) => (
                    <div
                        key={index}
                        className={`relative pl-8 pb-8 cursor-pointer opacity-0 ${animate ? 'animate-fadeIn' : ''}`}
                        style={{
                            color: index === selectedDayIndex ? operatorColor : 'inherit',
                            animationDelay: `${index * 100}ms`,
                            animationFillMode: 'forwards',
                        }}
                        onClick={() => onDayClick(index)}
                    >
                        {index < tourPackage.itinerary.length - 1 && (
                            <div
                                className="absolute left-[0.75rem] top-2 bottom-0 w-0.5"
                                style={{ backgroundColor: operatorColor }}
                            ></div>
                        )}
                        <div
                            className="absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center border-2"
                            style={{
                                borderColor: operatorColor,
                                backgroundColor: index === selectedDayIndex ? operatorColor : '#F9F9F9',
                            }}
                        ></div>
                        <div>
                            <div
                                className={classNames([
                                    'text-lg',
                                    index === selectedDayIndex ? 'text-[#11181E]' : 'text-[#AFAFAF]',
                                    index === selectedDayIndex ? 'font-medium' : 'font-normal',
                                ])}
                            >
              <span className={index === selectedDayIndex ? 'font-bold' : 'font-normal'}>
                Dia {day.day}{!isMobile ? '.' : ''}
              </span>{' '}
                                {!isMobile && day.title}
                            </div>
                        </div>
                    </div>
                ))
            );
        }
    };

    return (
        <div className="flex flex-col md:flex-row w-full h-full overflow-y-scroll lg:overflow-hidden relative"
             onTouchStart={(e) => e.stopPropagation()}
             onTouchMove={(e) => e.stopPropagation()}
        >
            {/* mobile top image*/}
            {isMobile && (
                <div className="w-full h-[140px] rounded-b-2xl" style={{
                    backgroundImage: tourPackage.mainImage
                        ? `url(${encodeURI(decodeURI(tourPackage.mainImage.file))})`
                        : '',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
            )}


            {/* Left Section - 40% width */}
            <div className="w-full flex-1 min-h-0 md:w-[45%] p-8 px-6 md:px-8 pb-2 md:pb-8 text-left md:pt-16 md:px-[70px] bg-[#F7F6F2]">
                {/* Tour Package Information */}
                <div className="md:mb-6 pt-0 md:pt-6 h-full">
                    <div className="text-md uppercase md:text-xl md:font-medium mb-1 md:pt-6">{tourPackage.category}</div>
                    <h2 className="text-xl md:text-2xl font-semibold md:font-black mb-1">
                        {tourPackage.name}
                    </h2>
                    {tourPackage.subtitle && (
                        <p className="text-lg font-normal md:font-semibold md:mb-4">{tourPackage.subtitle}</p>
                    )}

                    {/* Timeline with Itinerary Days - With staggered animation */}

                    { displayItineraryMap ?
                        <div className="h-[80%] w-full">
                            {!tourPackage.mapImageDigital?.file ? (
                            <ItineraryMap version="web" tourOperator={tourOperator} itineraryDays={tourPackage.itinerary}
                                      countryCodes={tourPackage.countryCodes.split(',')}
                        />) : (
                                        <SVGComponent
                                            svgUrl={tourPackage.mapImageDigital.file}
                                            className="w-full h-full object-cover"
                                        />
                                    ) }
                            </div>  : !hideDayTimeline && (
                        <div className="mt-2 md:mt-8">
                        {getTimeline()}
                    </div>)}
                </div>
            </div>

            {/* Right Section - 55% width */}
            <div className="w-full md:w-[55%] h-[140px] md:h-full flex flex-col md:pb-16 bg-white overflow-y-scroll">
                {/* Background Image - No animation needed */}
                {!isMobile && (
                <div className="min-h-[270px] max-h-[270px] h-full rounded-b-3xl" style={{
                    backgroundImage: tourPackage.mainImage
                        ? `url(${encodeURI(decodeURI(tourPackage.mainImage.file))})`
                        : '',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div> )}

                {selectedDay && (
                    <>
                        {/* Day details with staggered animation */}
                        <div className="p-10 px-6 md:px-8 pb-6 md:flex-grow text-start">

                            {displayItineraryMap && !hideDayTimeline && (<div className="mb-4">{getTimeline()}</div>)

                            }
                            {/* Day title with animation */}
                            <h3
                                className={`text-2xl font-extrabold mb-0 opacity-0 ${animateRightPanel ? 'animate-fadeIn' : ''}`}
                                style={{
                                    animationDelay: '100ms',
                                    animationFillMode: 'forwards'
                                }}
                            >
                                {!isMobile && !hideDayTimeline && `Día ${selectedDay.day}`}
                            </h3>

                            {/* Day subtitle with animation */}

                            <h3
                                className={`text-lg md:text-2xl font-medium mb-4 opacity-0 ${animateRightPanel ? 'animate-fadeIn' : ''}`}
                                style={{
                                    animationDelay: '200ms',
                                    animationFillMode: 'forwards'
                                }}
                            >
                                {selectedDay.title}
                            </h3>

                            {/* Description with animation */}
                            <div
                                className={`prose max-w-none text-sm opacity-0 ${animateRightPanel ? 'animate-fadeIn' : ''}`}
                                style={{
                                    animationDelay: '300ms',
                                    animationFillMode: 'forwards'
                                }}
                            >
                                <p className="leading-6.5">{selectedDay.description}</p>
                            </div>
                        </div>

                        {/* Image Carousel with animation */}
                        {selectedDay.images && selectedDay.images.length > 0 && (
                            <div
                                className={`h-[300px] md:h-[400px] p-10 pt-0 md:mt-auto flex-shrink-0 relative pb-20 md:pb-10 opacity-0 ${animateRightPanel ? 'animate-fadeIn' : ''}`}
                                style={{
                                    animationDelay: '400ms',
                                    animationFillMode: 'forwards'
                                }}
                            >
                                {/* Left Arrow */}
                                <div onClick={handlePrevious}
                                     className={`absolute top-1/3 md:top-45 left-2 md:left-2 z-10`}
                                >
                                    <div
                                        className="size-8 flex justify-center items-center cursor-pointer rounded-sm"
                                        style={{backgroundColor: 'transparent'}}
                                    >
                                        <ArrowLeftIcon className="size-6"/>
                                    </div>
                                </div>
                                <Swiper
                                    key={selectedDay.title + "swiper"}
                                    onSwiper={setSwiperRef}
                                    modules={[Navigation, Pagination, EffectFade, Autoplay]}
                                    effect={'fade'}
                                    autoplay={
                                        {
                                            delay: 5000,
                                            disableOnInteraction: false,
                                        }
                                    }
                                    pagination={{
                                        clickable: true,
                                        dynamicBullets: true,
                                    }}
                                    style={{
                                        '--swiper-navigation-color': operatorColor,
                                        '--swiper-pagination-color': '#1A1A1A',
                                    }}

                                    spaceBetween={0}
                                    slidesPerView={1}
                                    centeredSlides={true}
                                    className="h-full w-full"
                                >
                                    {selectedDay.images.map((image, index) => (
                                        <SwiperSlide key={index} className="h-full w-full py-4 md:py-8">
                                            <img
                                                src={image?.file}
                                                alt={`Día ${selectedDay.day} - Imagen ${index + 1}`}
                                                className="w-full h-full object-cover rounded-2xl"
                                            />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                {/* Agregamos los botones manualmente */}
                                {/* Right Arrow */}
                                <div onClick={handleNext}
                                     className={`absolute top-1/3 md:top-45 right-2 md:right-2 z-10`}
                                >
                                    <div

                                        className="size-8 flex justify-center items-center cursor-pointer rounded-sm"
                                        style={{backgroundColor: 'transparent'}}
                                    >
                                        <ArrowRightIcon className="size-6"/>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/*tourPackages partners */}
                        {tourPackage.partnersTitle && (
                        <div className={classNames([`h-10 w-full mb-4 py-2 text-start px-10 text-xl border-t-2 pt-8 pb-8  font-semibold`])} style={{
                            borderColor: operatorColor,
                        }}>{tourPackage.partnersTitle}</div>)}
                        {tourPackage.partners && tourPackage.partners.map((partner) => (
                            <div key={partner.title} className="flex flex-col gap-4 mb-8">
                                <div className="font-semibold text-start px-10">{partner.title}</div>
                                <div className="text-sm text-start px-10">{partner.description}</div>
                                {partner.image && (
                                    <div className="flex justify-start px-10  w-full">
                                        <img
                                            src={partner.image.file}
                                            alt={partner.image.title}
                                            className=" w-full"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};
